/**
 * Skin Issue Detection Utility
 * 
 * Maps scan analysis data to detected skin issue IDs based on plan tier.
 * Uses safe, non-medical language and only detects issues appropriate for each tier.
 */

import type {
  AcneSeverity,
  RegionAcneSummary,
  SkinAnalysisResult,
  ScanAnalysis,
} from '../services/scanService';
import type { PlanTier } from '../config/skinIssues';
import { SKIN_ISSUES } from '../config/skinIssues';

/**
 * Detect skin issues based on scan analysis data
 */
export function detectSkinIssues(
  analysis: ScanAnalysis,
  planTier: PlanTier = 'plus'
): string[] {
  const detectedIssueIds: string[] = [];
  const glowScore = analysis.skinHealthIndex;
  const acneSeverity = analysis.acneSeverity;
  const acneAnalysis = analysis.acneAnalysis;
  const estimatedPH = analysis.estimatedPH;
  const textureNotes = analysis.textureNotes?.toLowerCase() || '';
  const hasInflammation = acneAnalysis?.hasInflammation || false;
  
  // Get regions data
  const regions = acneAnalysis?.regions || [];
  const totalInflamed = regions.reduce((sum, r) => sum + r.activeLesionCount, 0);
  const totalComedones = regions.reduce((sum, r) => sum + r.comedoneCount, 0);
  const hasHyperpigmentation = regions.some(r => r.hyperpigmentationLevel !== 'none');
  
  // ========== PLUS PLAN DETECTIONS (Acne & Oil Focus) ==========
  
  // Acne types
  if (totalComedones > 0) {
    // Blackheads (open comedones)
    if (totalComedones >= 3) {
      detectedIssueIds.push('blackheads');
    }
    // Whiteheads (closed comedones)
    if (totalComedones >= 1) {
      detectedIssueIds.push('whiteheads');
    }
  }
  
  // Papules (small raised bumps without pus)
  if (totalInflamed > 0 && totalInflamed <= 10) {
    detectedIssueIds.push('papules');
  }
  
  // Pustules (inflamed bumps with pus)
  if (hasInflammation && totalInflamed > 0) {
    detectedIssueIds.push('pustules');
  }
  
  // Mild inflammatory acne
  if (acneSeverity === 'mild' || acneSeverity === 'mild_to_moderate') {
    detectedIssueIds.push('mild_inflammatory_acne');
  }
  
  // Oily skin indicators
  if (estimatedPH && estimatedPH < 4.8) {
    // Lower pH can indicate more acidic/oily skin
    detectedIssueIds.push('oily_skin');
  }
  if (glowScore < 60 && totalComedones > 5) {
    detectedIssueIds.push('excess_sebum');
  }
  
  // Enlarged pores
  if (textureNotes.includes('pore') || textureNotes.includes('enlarged')) {
    detectedIssueIds.push('enlarged_pores');
  }
  
  // ========== PRO PLAN DETECTIONS (Adds Barrier, Sensitivity, Texture) ==========
  if (planTier === 'pro' || planTier === 'elite') {
    
    // Dry skin indicators
    if (estimatedPH && estimatedPH > 5.5) {
      // Higher pH indicates more alkaline/dry skin
      detectedIssueIds.push('dry_skin');
    }
    if (glowScore < 70 && textureNotes.includes('dry')) {
      detectedIssueIds.push('dehydrated_skin');
    }
    
    // Flaking
    if (textureNotes.includes('flake') || textureNotes.includes('peel')) {
      detectedIssueIds.push('flaking');
    }
    
    // Tightness (inferred from dry skin indicators)
    if (estimatedPH && estimatedPH > 5.5) {
      detectedIssueIds.push('tightness');
    }
    
    // Rough texture
    if (textureNotes.includes('rough') || textureNotes.includes('uneven')) {
      detectedIssueIds.push('rough_texture');
      detectedIssueIds.push('uneven_texture');
    }
    
    // Compromised barrier indicators
    if ((estimatedPH && (estimatedPH > 5.8 || estimatedPH < 4.5)) || 
        (glowScore < 65 && hasInflammation)) {
      detectedIssueIds.push('compromised_barrier');
    }
    
    // Sensitive skin tendencies
    if (estimatedPH && estimatedPH < 4.8 && hasInflammation && acneSeverity !== 'severe') {
      detectedIssueIds.push('sensitive_skin_tendencies');
    }
    
    // Redness (non-medical)
    if (hasInflammation && acneSeverity !== 'severe') {
      detectedIssueIds.push('redness_non_medical');
    }
    
    // Product irritation patterns (inferred)
    if (hasInflammation && glowScore < 70 && acneSeverity === 'mild') {
      detectedIssueIds.push('product_irritation_patterns');
    }
    
    // Over-exfoliation indicators
    if (textureNotes.includes('irritat') && estimatedPH && estimatedPH < 4.5) {
      detectedIssueIds.push('over_exfoliation_indicators');
    }
    
    // Early PIH
    if (hasHyperpigmentation) {
      const hyperpigmentationLevel = regions.find(r => r.hyperpigmentationLevel !== 'none')?.hyperpigmentationLevel;
      if (hyperpigmentationLevel === 'mild') {
        detectedIssueIds.push('early_pih');
      }
    }
    
    // Dullness
    if (glowScore < 70 && !hasInflammation) {
      detectedIssueIds.push('dullness');
    }
  }
  
  // ========== ELITE PLAN DETECTIONS (Comprehensive) ==========
  if (planTier === 'elite') {
    
    // Moderate acne patterns
    if (acneSeverity === 'moderate' || acneSeverity === 'moderate_to_severe') {
      detectedIssueIds.push('moderate_acne_patterns');
    }
    
    // Hormonal acne distribution (inferred from jawline/chin focus)
    const jawlineChinCount = regions
      .filter(r => r.region === 'jawline' || r.region === 'chin')
      .reduce((sum, r) => sum + r.activeLesionCount, 0);
    if (jawlineChinCount > totalInflamed * 0.4 && totalInflamed > 10) {
      detectedIssueIds.push('hormonal_acne_distribution');
    }
    
    // Cystic-like tendencies (non-diagnostic)
    if (acneSeverity === 'severe' && totalInflamed > 50) {
      detectedIssueIds.push('cystic_like_tendencies');
    }
    
    // Acne scarring
    if (textureNotes.includes('scar') || textureNotes.includes('rolling')) {
      detectedIssueIds.push('acne_scarring_rolling');
    }
    if (textureNotes.includes('boxcar') || textureNotes.includes('pitted')) {
      detectedIssueIds.push('acne_scarring_boxcar');
    }
    
    // Hyperpigmentation
    if (hasHyperpigmentation) {
      detectedIssueIds.push('hyperpigmentation');
      const hyperpigmentationLevel = regions.find(r => r.hyperpigmentationLevel !== 'none')?.hyperpigmentationLevel;
      if (hyperpigmentationLevel === 'moderate' || hyperpigmentationLevel === 'marked') {
        detectedIssueIds.push('pih');
      }
    }
    
    // Sun spots / age spots
    if (hasHyperpigmentation && glowScore > 70 && estimatedPH && estimatedPH > 5.5) {
      detectedIssueIds.push('sun_spots');
    }
    
    // Uneven skin tone
    if (hasHyperpigmentation || textureNotes.includes('uneven')) {
      detectedIssueIds.push('uneven_skin_tone');
    }
    
    // Melasma-like patterns (informational only)
    if (hasHyperpigmentation && regions.some(r => 
      (r.region === 'forehead' || r.region === 'left_cheek' || r.region === 'right_cheek') &&
      r.hyperpigmentationLevel !== 'none'
    )) {
      detectedIssueIds.push('melasma_like_patterns');
    }
    
    // Fine lines
    if (glowScore >= 70 && glowScore < 85 && textureNotes.includes('line')) {
      detectedIssueIds.push('fine_lines');
    }
    
    // Wrinkles
    if (glowScore >= 70 && textureNotes.includes('wrinkle')) {
      detectedIssueIds.push('wrinkles');
    }
    
    // Loss of elasticity (inferred)
    if (glowScore >= 75 && glowScore < 90 && textureNotes.includes('lax')) {
      detectedIssueIds.push('loss_of_elasticity');
      detectedIssueIds.push('skin_laxity');
    }
    
    // Crepey texture
    if (textureNotes.includes('crepe') || textureNotes.includes('crinkled')) {
      detectedIssueIds.push('crepey_texture');
    }
    
    // Enlarged pore severity grading
    if (textureNotes.includes('pore') || textureNotes.includes('enlarged')) {
      detectedIssueIds.push('enlarged_pore_severity');
    }
    
    // Chronic redness patterns
    if (hasInflammation && glowScore < 70 && estimatedPH && estimatedPH < 4.8) {
      detectedIssueIds.push('chronic_redness_patterns');
    }
    
    // Eczema-prone indicators (inferred)
    if (estimatedPH && estimatedPH < 4.5 && hasInflammation && 
        textureNotes.includes('dry') && glowScore < 65) {
      detectedIssueIds.push('eczema_prone_indicators');
    }
    
    // Rosacea-prone indicators (inferred)
    if (hasInflammation && estimatedPH && estimatedPH < 4.8 && 
        !(acneSeverity === 'moderate' || acneSeverity === 'severe') &&
        glowScore < 70) {
      detectedIssueIds.push('rosacea_prone_indicators');
    }
    
    // UV damage indicators
    if (hasHyperpigmentation && glowScore > 70 && estimatedPH && estimatedPH > 5.5) {
      detectedIssueIds.push('uv_damage_indicators');
    }
    
    // Environmental stress markers
    if (glowScore < 70 && textureNotes.includes('dull') && !hasInflammation) {
      detectedIssueIds.push('environmental_stress_markers');
    }
  }
  
  // Remove duplicates and filter to only include issues available for the plan tier
  const uniqueIssueIds = Array.from(new Set(detectedIssueIds));
  const tierOrder: PlanTier[] = ['plus', 'pro', 'elite'];
  const currentTierIndex = tierOrder.indexOf(planTier);
  
  return uniqueIssueIds.filter(issueId => {
    const issue = SKIN_ISSUES.find(i => i.id === issueId);
    if (!issue) return false;
    const issueTierIndex = tierOrder.indexOf(issue.planTier);
    return issueTierIndex <= currentTierIndex;
  });
}

